"use client";

import { ReactNode, useState } from "react";
import { toast } from "sonner";
import { BookOpenIcon, LoaderIcon, PlayIcon } from "lucide-react";

import { clientPost } from "@/service/client-api/client-api";
import { createPath } from "@/service/routing/routing";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";

/** Wybór źródeł pytań w kształcie przyjmowanym przez POST /exam/browse. */
type BrowseSources = {
  allCem?: boolean;
  allHistoric?: boolean;
  sessionIds?: number[];
  specializationIds?: number[];
};

/** Czego dotyczy zestaw — decyduje tylko o nazwaniu go w nagłówku modalu. */
type StartScope = "exam" | "specialization" | "database";

const SCOPE_COPY: Record<StartScope, { heading: string; sources: string }> = {
  exam: {
    heading: "Ucz się z tego egzaminu",
    sources: "Zestaw obejmuje cały egzamin.",
  },
  specialization: {
    heading: "Ucz się z tej dziedziny",
    sources: "Zestaw obejmuje wszystkie pytania z tej dziedziny.",
  },
  database: {
    heading: "Ucz się z bazy pytań CEM",
    sources: "Zestaw obejmuje całą bazę pytań CEM.",
  },
};

/**
 * Wybór, co zrobić z zestawem pytań: rozwiązać go jako podejście albo przejrzeć pytania
 * z widoczną odpowiedzią. Oba wejścia były wcześniej osobnymi przyciskami (podejście w nagłówku,
 * strzałka „od tego pytania” w wierszu pytania) — teraz są jedną decyzją w modalu.
 *
 * Punkt startowy: cały zestaw albo cały zestaw, ale od wybranego pytania. Dla podejścia numer
 * pytania siedzi w URL-u, a dla przeglądania pozycję ustala backend — dostaje ID pytania
 * (`startQuestionId`) i sam zakłada na nim zakładkę, bo zestaw może łączyć wiele źródeł
 * i wtedy pozycja nie jest numerem z arkusza.
 *
 * Modal stoi wszędzie tam, gdzie wcześniej był goły przycisk „Rozpocznij podejście”: na arkuszu
 * archiwalnym (źródło = sesja), na stronie dziedziny z bazy CEM (źródło = dziedzina) i na liście
 * dziedzin, gdzie podejście brało całą bazę (źródło = `allCem`). Źródła składa wołający —
 * `params` idą do endpointu podejścia, a `browseSources` wprost do POST /exam/browse.
 */
export const ExamPageTypeStartDialog = ({
  attemptPathname,
  params,
  browseSources,
  scope,
  startQuestion,
  open,
  onOpenChange,
  children,
}: {
  attemptPathname: string;
  params: Record<string, string | number>;
  // Brak źródeł = nie ma z czego złożyć zestawu i sekcja przeglądania wypada z modalu.
  browseSources?: BrowseSources | null;
  scope: StartScope;
  startQuestion?: { number: number; questionId: number };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [pending, setPending] = useState<null | "attempt" | "browse">(null);
  const [fromStart, setFromStart] = useState(false);

  const isControlled = undefined !== open;
  const isOpen = isControlled ? open : uncontrolledOpen;
  const setIsOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setUncontrolledOpen(next);
    }

    // Kolejne otwarcie zaczyna od tego samego stanu co pierwsze: modal nie remontuje się
    // między pytaniami, więc bez resetu zostałby wybór z poprzedniego kliknięcia.
    if (!next) {
      setFromStart(false);
      setPending(null);
    }
  };

  // Bez wskazanego pytania jest tylko jeden punkt startowy — początek egzaminu.
  const startAt = startQuestion && !fromStart ? startQuestion : null;

  const startAttempt = async () => {
    setPending("attempt");

    try {
      const { id } = (await clientPost(attemptPathname, params)) as {
        id: number;
      };

      window.location.pathname = createPath("exam.attempt.question", {
        attempt: id,
        questionNumber: startAt?.number ?? 1,
      });
    } catch (error) {
      toast.error(
        (error as { error?: string })?.error ??
          "Nie udało się rozpocząć podejścia",
      );
      setPending(null);
    }
  };

  const startBrowse = async () => {
    if (!browseSources) return;

    setPending("browse");

    try {
      const { id } = (await clientPost(
        "exam.browse.create",
        {},
        {
          ...browseSources,
          ...(startAt ? { startQuestionId: startAt.questionId } : {}),
        },
      )) as { id: string };

      window.location.pathname = createPath("exam.browse.session", {
        session: id,
      });
    } catch (error) {
      toast.error(
        (error as { error?: string })?.error ??
          "Nie udało się utworzyć zestawu do przeglądania",
      );
      setPending(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children ? <DialogTrigger>{children}</DialogTrigger> : null}
      {/* Zapas na paddingu, bo wnętrze dialogu ma overflow-hidden: przyciski rosną przy zmianie
          etykiety i na hoverze, a przy ciaśniejszym marginesie ich krawędzie były przycinane. */}
      <DialogContent className="w-[min(calc(100vw-2rem),490px)] p-8">
        <div className="mb-4">
          <h3 className="mb-1.5 text-lg leading-6 font-semibold">
            {SCOPE_COPY[scope].heading}
          </h3>
          <p className="text-muted-foreground text-sm leading-5">
            {SCOPE_COPY[scope].sources} Wybierz, od którego pytania zaczynasz i
            w jakim trybie.
          </p>
        </div>

        {startQuestion && (
          <div className="mb-4 flex flex-col gap-2">
            <p className="text-sm font-semibold">Punkt startowy</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={fromStart ? "outlinePrimary" : "outline"}
                onClick={() => setFromStart(true)}
              >
                Od początku
              </Button>
              <Button
                variant={fromStart ? "outline" : "outlinePrimary"}
                onClick={() => setFromStart(false)}
              >
                Od pytania {startQuestion.number}
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 p-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Rozwiąż jako egzamin</p>
              <p className="text-muted-foreground text-xs leading-4">
                Podejście z odpowiedziami do zaznaczenia i wynikiem na koniec.
              </p>
            </div>
            <Button
              variant="blue"
              disabled={!!pending}
              onClick={() => startAttempt()}
            >
              {"attempt" === pending ? (
                <LoaderIcon className="animate-spin" />
              ) : (
                <PlayIcon />
              )}
              Rozpocznij podejście
            </Button>
          </div>

          {!!browseSources && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">Przeglądaj pytania</p>
                <p className="text-muted-foreground text-xs leading-4">
                  Bez rozwiązywania — poprawna odpowiedź i omówienie widoczne od
                  razu.
                </p>
              </div>
              <Button
                variant="outline"
                disabled={!!pending}
                onClick={() => startBrowse()}
              >
                {"browse" === pending ? (
                  <LoaderIcon className="animate-spin" />
                ) : (
                  <BookOpenIcon />
                )}
                Przeglądaj zestaw
              </Button>
            </div>
          )}
        </div>

        <DialogClose />
      </DialogContent>
    </Dialog>
  );
};
